import {
  ChangeEvent,
  DragEvent,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {useTranslation} from 'react-i18next';

import {ADDITIONAL_DOCUMENTS_EVENTS} from '@/app/auth/events/AdditionalDocumentsEvents';
import {showSnackbar} from '@/app/shared-components/Snackbar/SnackbarProvider';
import {SnackbarType} from '@/app/shared-components/Snackbar/SnackbarTypes';
import {useLoading} from '@/app/zustand/useLoading';
import {CKycDocumentTypes} from '@/dataSource/src/api/kyc/constants';
import {KycAdditionalDocumentType} from '@/dataSource/src/api/kyc/types';
import {useKycRepository} from '@/dataSource/src/api/kyc/useRepository';
import logError from '@/utils/errors/logError';

import {useTrackers} from './trackers';

interface IStepDocumentUploader {
  enabled: boolean;
  documentType: KycAdditionalDocumentType;
  uploadEvent: string;
  errorEvent: string;
  viewEvent: string;
}

type UploadedFile = {
  file: File | null;
  fileName: string;
};

export type UseActionsProps = {
  acceptFiles?: string;
  sizeMb?: number;
  onSubmit: () => void;
};

const steps: IStepDocumentUploader[] = [
  {
    enabled: true,
    documentType: CKycDocumentTypes.DocumentUtilityBill,
    uploadEvent:
      ADDITIONAL_DOCUMENTS_EVENTS.UPLOAD_KYC_ADDITIONAL_DOCUMENTS_UTILITY_BILL,
    errorEvent:
      ADDITIONAL_DOCUMENTS_EVENTS.ERROR_KYC_ADDITIONAL_DOCUMENTS_UTILITY_BILL,
    viewEvent:
      ADDITIONAL_DOCUMENTS_EVENTS.VIEW_KYC_ADDITIONAL_DOCUMENTS_UTILITY_BILL,
  },
  {
    enabled: false,
    documentType: CKycDocumentTypes.DocumentPhoneBill,
    uploadEvent:
      ADDITIONAL_DOCUMENTS_EVENTS.UPLOAD_KYC_ADDITIONAL_DOCUMENTS_PHONE_BILL,
    errorEvent:
      ADDITIONAL_DOCUMENTS_EVENTS.ERROR_KYC_ADDITIONAL_DOCUMENTS_PHONE_BILL,
    viewEvent:
      ADDITIONAL_DOCUMENTS_EVENTS.VIEW_KYC_ADDITIONAL_DOCUMENTS_PHONE_BILL,
  },
];

const enabledSteps = steps.filter(step => step.enabled);

export const defaultAcceptedFiles =
  'image/*,application/pdf,image/jpeg,image/png';

export const useActions = ({
  acceptFiles = defaultAcceptedFiles,
  sizeMb = 5,
  onSubmit,
}: UseActionsProps) => {
  //#region [---- [PROPERTIES] ----]
  const {t} = useTranslation();
  const abortControllerRef = useRef<AbortController | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const dropZoneRef = useRef<HTMLDivElement | null>(null);
  const cssDrag = 'drag-over';

  const {handlePutAdditionalDocuments} = useKycRepository();
  const {trackAdditionalDocuments, trackAdditionalDocumentsContinue} =
    useTrackers();
  const {setSkipLoading} = useLoading();
  //#endregion

  //#region [---- [STATES] ----]
  const [fileSize, setFileSize] = useState(0);
  const [fileFormat, setFileFormat] = useState('');
  const [uploadingSteps, setUploadingSteps] = useState(
    Array(enabledSteps.length).fill(false),
  );
  const [currentStep, setCurrentStep] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState<
    Array<UploadedFile | null>
  >([
    {file: null, fileName: ''},
    {file: null, fileName: ''},
  ]);
  const [uploading, setUploading] = useState(false);
  //#endregion

  //#region [---- [DERIVED VALUES] ----]
  const isFirstStep = useMemo(() => currentStep === 0, [currentStep]);
  const isLastStep = useMemo(
    () => currentStep === enabledSteps.length - 1,
    [currentStep],
  );

  const canUploadFile = useMemo(() => {
    return !uploadedFiles[currentStep]?.file && !uploadingSteps[currentStep];
  }, [currentStep, uploadedFiles, uploadingSteps]);

  const isUploadingFile = useMemo(() => {
    return uploadingSteps[currentStep] && uploading;
  }, [uploadingSteps, currentStep, uploading]);

  const isFileUploaded = useMemo(() => {
    return uploadedFiles[currentStep]?.file && !uploading;
  }, [currentStep, uploadedFiles, uploading]);

  const isDisabledButton = useMemo(() => {
    return !uploadedFiles[currentStep]?.file || uploading;
  }, [currentStep, uploadedFiles, uploading]);

  const currentFileName = useMemo(() => {
    return uploadedFiles[currentStep]?.fileName;
  }, [currentStep, uploadedFiles]);
  //#endregion

  //#region [---- [ACTIONS] ----]
  const addClassDrag = useCallback(() => {
    if (dropZoneRef.current) {
      dropZoneRef.current.classList.add(cssDrag);
    }
  }, []);

  const removeClassDrag = useCallback(() => {
    if (dropZoneRef.current) {
      dropZoneRef.current.classList.remove(cssDrag);
    }
  }, []);

  const onContinue = useCallback(() => {
    trackAdditionalDocumentsContinue(
      ADDITIONAL_DOCUMENTS_EVENTS.SELECT_KYC_ADDITIONAL_DOCUMENTS_UTILITY_BILL_CONTINUE,
      fileFormat,
      fileSize.toString(),
    );
    setCurrentStep(prev => prev + 1);
  }, [fileFormat, fileSize, trackAdditionalDocumentsContinue]);

  const onBack = useCallback(() => setCurrentStep(prev => prev - 1), []);

  const triggerFileInput = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const validateFile = useCallback(
    (file: File): boolean => {
      if (!file) {
        showSnackbar({
          summary: t('kyc.idv_docs.uploader.snackbar.no_file_selected.summary'),
          message: t('kyc.idv_docs.uploader.snackbar.no_file_selected.message'),
          variant: SnackbarType.WARNING,
        });
        return false;
      }
      if (file.size === 0) {
        showSnackbar({
          summary: t('kyc.idv_docs.uploader.snackbar.empty_file.summary'),
          message: t('kyc.idv_docs.uploader.snackbar.empty_file.message'),
          variant: SnackbarType.WARNING,
        });
        return false;
      }
      if (file.size > sizeMb * 1024 * 1024) {
        showSnackbar({
          summary: t('kyc.idv_docs.uploader.snackbar.file_too_large.summary'),
          message: t('kyc.idv_docs.uploader.snackbar.file_too_large.message', {
            sizeMb,
          }),
          variant: SnackbarType.WARNING,
        });
        return false;
      }
      if (!acceptFiles.split(',').includes(file.type)) {
        showSnackbar({
          summary: t(
            'kyc.idv_docs.uploader.snackbar.unsupported_file_type.summary',
          ),
          message: t(
            'kyc.idv_docs.uploader.snackbar.unsupported_file_type.message',
            {sizeMb},
          ),
          variant: SnackbarType.WARNING,
        });
        return false;
      }
      setFileFormat(file.type);
      setFileSize(file.size / 1024 / 1024);
      return true;
    },
    [acceptFiles, sizeMb, t],
  );

  const updateUploadingStep = useCallback(
    (stepIndex: number, isUploading: boolean) => {
      setUploadingSteps(prev => {
        const updatedSteps = [...prev];
        updatedSteps[stepIndex] = isUploading;
        return updatedSteps;
      });
      setUploading(isUploading);
    },
    [],
  );

  const handleFileUpload = useCallback(
    async (file: File) => {
      if (!validateFile(file)) return;

      try {
        setSkipLoading(true);
        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        updateUploadingStep(currentStep, true);

        setUploadedFiles(prev => {
          const updatedFiles = [...prev];
          updatedFiles[currentStep] = {file, fileName: file.name};
          return updatedFiles;
        });

        await handlePutAdditionalDocuments({
          documentType: enabledSteps[currentStep].documentType,
          file: file,
          signal: abortController.signal,
        });

        updateUploadingStep(currentStep, false);
        trackAdditionalDocuments(enabledSteps[currentStep].uploadEvent);
      } catch (error) {
        logError(error, {
          customTitle: 'KycDocumentUploader - Error uploading file',
          fileName: 'KycDocumentUploader.tsx',
          functionName: 'handleFileUpload',
          data: {
            fileName: file.name,
            fileSize: file.size,
            fileType: file.type,
            currentStep: currentStep,
          },
        });
        trackAdditionalDocuments(enabledSteps[currentStep].errorEvent, error);
        showSnackbar({
          message:
            error?.message ||
            t('kyc.idv_docs.uploader.snackbar.upload_error_default'),
          variant: SnackbarType.ERROR,
        });

        updateUploadingStep(currentStep, false);
        setUploadedFiles(prev => {
          const updatedFiles = [...prev];
          updatedFiles[currentStep] = null;
          return updatedFiles;
        });
      }
    },
    [
      currentStep,
      handlePutAdditionalDocuments,
      setSkipLoading,
      trackAdditionalDocuments,
      updateUploadingStep,
      validateFile,
      t,
    ],
  );

  const onFileSelected = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        await handleFileUpload(file);
      }
      event.target.value = null;
    },
    [handleFileUpload],
  );

  const onDragLeave = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      removeClassDrag();
    },
    [removeClassDrag],
  );

  const onDragOver = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      addClassDrag();
    },
    [addClassDrag],
  );

  const onDrop = useCallback(
    async (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      removeClassDrag();
      const file = event.dataTransfer.files?.[0];
      if (file) {
        await handleFileUpload(file);
      }
    },
    [handleFileUpload, removeClassDrag],
  );

  const cancelUpload = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    updateUploadingStep(currentStep, false);
    setUploadedFiles(prev => {
      const updatedFiles = [...prev];
      updatedFiles[currentStep] = null;
      return updatedFiles;
    });
  }, [currentStep, updateUploadingStep]);

  const removeFile = useCallback(() => {
    setUploadedFiles(prev => {
      const updatedFiles = [...prev];
      updatedFiles[currentStep] = {file: null, fileName: ''};
      return updatedFiles;
    });
  }, [currentStep]);

  const onSubmitForm = useCallback(() => {
    trackAdditionalDocumentsContinue(
      ADDITIONAL_DOCUMENTS_EVENTS.SELECT_KYC_ADDITIONAL_DOCUMENTS_PHONE_BILL_CONTINUE,
      fileFormat,
      fileSize.toString(),
    );
    onSubmit();
  }, [fileFormat, fileSize, onSubmit, trackAdditionalDocumentsContinue]);
  //#endregion

  //#region [---- [EFFECTS] ----]
  useEffect(() => {
    trackAdditionalDocuments(enabledSteps[currentStep].viewEvent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);
  //#endregion

  return {
    fileInputRef: fileInputRef as RefObject<HTMLInputElement>,
    dropZoneRef,
    acceptFiles,
    sizeMb,
    isFirstStep,
    isLastStep,
    canUploadFile,
    isUploadingFile,
    isFileUploaded,
    isDisabledButton,
    currentFileName,
    onBack,
    triggerFileInput,
    onFileSelected,
    onDragLeave,
    onDragOver,
    onDrop,
    cancelUpload,
    removeFile,
    onContinue,
    onSubmitForm,
  };
};
